import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import type {
  SkinAnalysisAdminConfigBundle,
  SkinAnalysisMetricDetailSection,
  SkinAnalysisMetricDetailItemDisplayType,
  SkinAnalysisMetricCopyConfig,
  SkinAnalysisMetricKey,
  SkinAnalysisMetricRangeCopy,
  SkinAnalysisRoutinePolicyStep,
  SkinAnalysisSurveyOption,
  SkinAnalysisSurveyQuestion,
} from '../../infrastructure/SkinAnalysisAdminConfigClient';
import { SkinAnalysisAdminConfigClient } from '../../infrastructure/SkinAnalysisAdminConfigClient';

type ConfigTab = 'metric-copy' | 'survey' | 'routine-policy';
type MetricScopedTab = 'metric-copy' | 'routine-policy';
type RoutineScheduleTag = 'AM' | 'PM';
type RoutineCategoryLevel = 1 | 2 | 3;

interface RoutineCategoryOption {
  id: string;
  label: string;
  children?: RoutineCategoryOption[];
}

const ROUTINE_CATEGORY_OPTIONS: RoutineCategoryOption[] = [
  {
    id: 'skincare',
    label: 'Skincare',
    children: [
      {
        id: 'cleanser',
        label: 'Cleanser',
        children: [
          { id: 'oil-cleanser', label: 'Oil cleanser' },
          { id: 'gel-cleanser', label: 'Gel cleanser' },
          { id: 'foam-cleanser', label: 'Foam cleanser' },
        ],
      },
      {
        id: 'serum',
        label: 'Serum',
        children: [
          { id: 'hydrating-serum', label: 'Hydrating serum' },
          { id: 'brightening-serum', label: 'Brightening serum' },
          { id: 'calming-serum', label: 'Calming serum' },
          { id: 'pore-serum', label: 'Pore serum' },
          { id: 'firming-serum', label: 'Firming serum' },
        ],
      },
      {
        id: 'treatment',
        label: 'Treatment',
        children: [
          { id: 'acne-treatment', label: 'Acne treatment' },
          { id: 'exfoliating-treatment', label: 'Exfoliating treatment' },
          { id: 'retinoid-treatment', label: 'Retinoid treatment' },
          { id: 'spot-treatment', label: 'Spot treatment' },
        ],
      },
      {
        id: 'moisturizer',
        label: 'Moisturizer',
        children: [
          { id: 'gel-cream', label: 'Gel cream' },
          { id: 'barrier-cream', label: 'Barrier cream' },
          { id: 'cream', label: 'Cream' },
        ],
      },
      {
        id: 'sunscreen',
        label: 'Sunscreen',
        children: [
          { id: 'chemical-sunscreen', label: 'Chemical sunscreen' },
          { id: 'mineral-sunscreen', label: 'Mineral sunscreen' },
        ],
      },
      {
        id: 'eye-care',
        label: 'Eye care',
        children: [
          { id: 'eye-serum', label: 'Eye serum' },
          { id: 'eye-cream', label: 'Eye cream' },
        ],
      },
    ],
  },
  {
    id: 'haircare',
    label: 'Hair care',
    children: [
      { id: 'shampoo', label: 'Shampoo' },
      { id: 'conditioner', label: 'Conditioner' },
      { id: 'scalp-care', label: 'Scalp care' },
      { id: 'hair-treatment', label: 'Hair treatment' },
      { id: 'leave-in', label: 'Leave-in care' },
    ],
  },
  {
    id: 'bodycare',
    label: 'Body care',
    children: [
      { id: 'body-cleanser', label: 'Body cleanser' },
      { id: 'body-lotion', label: 'Body lotion' },
      { id: 'body-sunscreen', label: 'Body sunscreen' },
      { id: 'body-treatment', label: 'Body treatment' },
    ],
  },
];

const cloneBundle = (bundle: SkinAnalysisAdminConfigBundle): SkinAnalysisAdminConfigBundle => {
  return JSON.parse(JSON.stringify(bundle)) as SkinAnalysisAdminConfigBundle;
};

const moveArrayItem = <T,>(items: T[], fromIndex: number, toIndex: number): T[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) {
    return items;
  }

  const next = [...items];
  const [item] = next.splice(fromIndex, 1);
  if (!item) {
    return items;
  }
  next.splice(toIndex, 0, item);
  return next;
};

const isNonEmptyText = (value?: string): boolean => {
  return Boolean(value?.trim());
};

const updateMetricCopyRange = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  index: number,
  patch: Partial<SkinAnalysisMetricRangeCopy>,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const current = next.metricCopies[metricKey].ranges[index];
  if (!current) {
    return next;
  }
  next.metricCopies[metricKey].ranges[index] = {
    ...current,
    ...patch,
  } as SkinAnalysisMetricRangeCopy;
  return next;
};

const updateMetricCopyConfig = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  patch: Partial<SkinAnalysisMetricCopyConfig>,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  next.metricCopies[metricKey] = {
    ...next.metricCopies[metricKey],
    ...patch,
  };
  return next;
};

const createMetricDetailSection = (): SkinAnalysisMetricDetailSection => ({
  sectionId: uuidv4(),
  title: 'New detail section',
  description: '',
  askSsolQuestion: '',
  items: [''],
  itemDisplayType: 'bullets',
});

const getMetricDetailSections = (copy: SkinAnalysisMetricRangeCopy): SkinAnalysisMetricDetailSection[] => {
  return Array.isArray(copy.detailSections) ? copy.detailSections : [];
};

const updateMetricCopyDetailSection = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  rangeIndex: number,
  sectionIndex: number,
  patch: Partial<SkinAnalysisMetricDetailSection>,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const copy = next.metricCopies[metricKey].ranges[rangeIndex];
  const section = copy?.detailSections?.[sectionIndex];
  if (!copy || !section) {
    return next;
  }

  copy.detailSections[sectionIndex] = {
    ...section,
    ...patch,
  };
  return next;
};

const addMetricCopyDetailSection = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  rangeIndex: number,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const copy = next.metricCopies[metricKey].ranges[rangeIndex];
  if (!copy) {
    return next;
  }
  copy.detailSections = [...getMetricDetailSections(copy), createMetricDetailSection()];
  return next;
};

const removeMetricCopyDetailSection = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  rangeIndex: number,
  sectionIndex: number,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const copy = next.metricCopies[metricKey].ranges[rangeIndex];
  if (!copy) {
    return next;
  }
  copy.detailSections = getMetricDetailSections(copy).filter((_, currentIndex) => currentIndex !== sectionIndex);
  return next;
};

const updateMetricCopyDetailItem = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  rangeIndex: number,
  sectionIndex: number,
  itemIndex: number,
  value: string,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const section = next.metricCopies[metricKey].ranges[rangeIndex]?.detailSections?.[sectionIndex];
  if (!section) {
    return next;
  }
  section.items = [...(section.items ?? [])];
  section.items[itemIndex] = value;
  return next;
};

const addMetricCopyDetailItem = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  rangeIndex: number,
  sectionIndex: number,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const section = next.metricCopies[metricKey].ranges[rangeIndex]?.detailSections?.[sectionIndex];
  if (!section) {
    return next;
  }
  section.items = [...(section.items ?? []), ''];
  return next;
};

const removeMetricCopyDetailItem = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  rangeIndex: number,
  sectionIndex: number,
  itemIndex: number,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const section = next.metricCopies[metricKey].ranges[rangeIndex]?.detailSections?.[sectionIndex];
  if (!section) {
    return next;
  }
  section.items = (section.items ?? []).filter((_, currentIndex) => currentIndex !== itemIndex);
  return next;
};

const updateSurveyQuestion = (
  bundle: SkinAnalysisAdminConfigBundle,
  index: number,
  patch: Partial<SkinAnalysisSurveyQuestion>,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const current = next.survey.questions[index];
  if (!current) {
    return next;
  }
  next.survey.questions[index] = {
    ...current,
    ...patch,
  } as SkinAnalysisSurveyQuestion;
  return next;
};

const addSurveyOption = (
  bundle: SkinAnalysisAdminConfigBundle,
  questionIndex: number,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const question = next.survey.questions[questionIndex];
  if (!question) {
    return next;
  }

  question.options = [
    ...question.options,
    {
      value: '',
      label: '',
      description: '',
      iconImageUrl: '',
      metricMappings: [],
    },
  ];
  return next;
};

const updateSurveyOption = (
  bundle: SkinAnalysisAdminConfigBundle,
  questionIndex: number,
  optionIndex: number,
  patch: Partial<SkinAnalysisSurveyOption>,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const question = next.survey.questions[questionIndex];
  const option = question?.options[optionIndex];
  if (!question || !option) {
    return next;
  }

  question.options[optionIndex] = {
    ...option,
    ...patch,
  };
  return next;
};

const updateSurveyOptionMetricMapping = (
  bundle: SkinAnalysisAdminConfigBundle,
  questionIndex: number,
  optionIndex: number,
  metricKey: string,
): SkinAnalysisAdminConfigBundle => {
  return updateSurveyOption(bundle, questionIndex, optionIndex, {
    metricMappings: metricKey ? [{ metricKey: metricKey as SkinAnalysisMetricKey, weight: 1 }] : [],
  });
};

const removeSurveyOption = (
  bundle: SkinAnalysisAdminConfigBundle,
  questionIndex: number,
  optionIndex: number,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const question = next.survey.questions[questionIndex];
  if (!question) {
    return next;
  }

  question.options = question.options.filter((_, currentIndex) => currentIndex !== optionIndex);
  return next;
};

const moveSurveyOption = (
  bundle: SkinAnalysisAdminConfigBundle,
  questionIndex: number,
  optionIndex: number,
  direction: -1 | 1,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const question = next.survey.questions[questionIndex];
  if (!question) {
    return next;
  }

  question.options = moveArrayItem(question.options, optionIndex, optionIndex + direction);
  return next;
};

const updateRoutineStep = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  index: number,
  patch: Partial<SkinAnalysisRoutinePolicyStep>,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const current = next.routinePolicies[metricKey].steps[index];
  if (!current) {
    return next;
  }
  const merged = {
    ...current,
    ...patch,
    selectedCategoryId:
      patch.category3Id ||
      patch.category2Id ||
      patch.category1Id ||
      current.category3Id ||
      current.category2Id ||
      current.category1Id,
  } as SkinAnalysisRoutinePolicyStep;
  next.routinePolicies[metricKey].steps[index] = merged;
  return next;
};

const updateRoutineStepScheduleTag = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  index: number,
  tag: RoutineScheduleTag,
  checked: boolean,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const current = next.routinePolicies[metricKey].steps[index];
  if (!current) {
    return next;
  }

  const tagSet = new Set(current.scheduleTags);
  if (checked) {
    tagSet.add(tag);
  } else {
    tagSet.delete(tag);
  }

  next.routinePolicies[metricKey].steps[index] = {
    ...current,
    scheduleTags: Array.from(tagSet).filter(
      (candidate): candidate is RoutineScheduleTag => candidate === 'AM' || candidate === 'PM',
    ),
  };
  return next;
};

const updateRoutineStepCategory = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  index: number,
  level: RoutineCategoryLevel,
  value: string,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const current = next.routinePolicies[metricKey].steps[index];
  if (!current) {
    return next;
  }

  if (level === 1) {
    current.category1Id = value;
    delete current.category2Id;
    delete current.category3Id;
    current.selectedCategoryId = value;
  }

  if (level === 2) {
    if (value) {
      current.category2Id = value;
    } else {
      delete current.category2Id;
    }
    delete current.category3Id;
    current.selectedCategoryId = value || current.category1Id;
  }

  if (level === 3) {
    if (value) {
      current.category3Id = value;
    } else {
      delete current.category3Id;
    }
    current.selectedCategoryId = value || current.category2Id || current.category1Id;
  }

  next.routinePolicies[metricKey].steps[index] = current;
  return next;
};

const normalizeRoutineStepOrder = (steps: SkinAnalysisRoutinePolicyStep[]): SkinAnalysisRoutinePolicyStep[] => {
  return steps.map((step, index) => ({
    ...step,
    stepOrder: index + 1,
  }));
};

const addRoutineStep = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const steps = next.routinePolicies[metricKey].steps;
  steps.push({
    stepOrder: steps.length + 1,
    name: 'New routine step',
    scheduleTags: ['AM'],
    description: '',
    category1Id: 'skincare',
    selectedCategoryId: 'skincare',
    reason: '',
  });
  next.routinePolicies[metricKey].steps = normalizeRoutineStepOrder(steps);
  return next;
};

const removeRoutineStep = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  index: number,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const steps = next.routinePolicies[metricKey].steps.filter((_, currentIndex) => currentIndex !== index);
  next.routinePolicies[metricKey].steps = normalizeRoutineStepOrder(steps);
  return next;
};

const moveRoutineStep = (
  bundle: SkinAnalysisAdminConfigBundle,
  metricKey: SkinAnalysisMetricKey,
  index: number,
  direction: -1 | 1,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  const steps = next.routinePolicies[metricKey].steps;
  next.routinePolicies[metricKey].steps = normalizeRoutineStepOrder(moveArrayItem(steps, index, index + direction));
  return next;
};

const addSurveyQuestion = (bundle: SkinAnalysisAdminConfigBundle): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  next.survey.questions.push({
    questionId: uuidv4(),
    label: 'New question',
    prompt: '',
    type: 'single',
    options: [],
  });
  return next;
};

const removeSurveyQuestion = (
  bundle: SkinAnalysisAdminConfigBundle,
  questionIndex: number,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  next.survey.questions = next.survey.questions.filter((_, currentIndex) => currentIndex !== questionIndex);
  return next;
};

const moveSurveyQuestion = (
  bundle: SkinAnalysisAdminConfigBundle,
  questionIndex: number,
  direction: -1 | 1,
): SkinAnalysisAdminConfigBundle => {
  const next = cloneBundle(bundle);
  next.survey.questions = moveArrayItem(next.survey.questions, questionIndex, questionIndex + direction);
  return next;
};

const findCategoryOption = (options: RoutineCategoryOption[], id?: string): RoutineCategoryOption | null => {
  if (!id) {
    return null;
  }
  for (const option of options) {
    if (option.id === id) {
      return option;
    }
    const child = findCategoryOption(option.children ?? [], id);
    if (child) {
      return child;
    }
  }
  return null;
};

const getChildCategoryOptions = (options: RoutineCategoryOption[], parentId?: string): RoutineCategoryOption[] => {
  return findCategoryOption(options, parentId)?.children ?? [];
};

export default function SkinAnalysisConfigPage() {
  const [client] = useState(() => new SkinAnalysisAdminConfigClient());
  const draftVersionRef = useRef(0);
  const [config, setConfig] = useState<SkinAnalysisAdminConfigBundle | null>(null);
  const [selectedMetrics, setSelectedMetrics] = useState<Record<MetricScopedTab, SkinAnalysisMetricKey>>({
    'metric-copy': 'breakouts',
    'routine-policy': 'breakouts',
  });
  const [activeTab, setActiveTab] = useState<ConfigTab>('metric-copy');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    client
      .getConfig()
      .then((bundle) => {
        if (!isMounted) {
          return;
        }
        const firstMetric = bundle.metrics[0]?.key ?? 'breakouts';
        setConfig(bundle);
        setSelectedMetrics({
          'metric-copy': firstMetric,
          'routine-policy': firstMetric,
        });
        setHasUnsavedChanges(false);
      })
      .catch((loadError) => {
        if (!isMounted) {
          return;
        }
        setError(loadError instanceof Error ? loadError.message : 'Failed to load Skin Analysis config.');
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [client]);

  const metricCopyMetric = selectedMetrics['metric-copy'];
  const routinePolicyMetric = selectedMetrics['routine-policy'];

  const metricCopyMetricDefinition = useMemo(() => {
    return config?.metrics.find((metric) => metric.key === metricCopyMetric) ?? null;
  }, [config, metricCopyMetric]);

  const routinePolicyMetricDefinition = useMemo(() => {
    return config?.metrics.find((metric) => metric.key === routinePolicyMetric) ?? null;
  }, [config, routinePolicyMetric]);

  const completionSummary = useMemo(() => {
    if (!config) {
      return null;
    }

    const metricCopyConfigs = Object.values(config.metricCopies);
    const metricCopyRanges = metricCopyConfigs.flatMap((metricCopy) => metricCopy.ranges);
    const completedMetricCopyRanges = metricCopyRanges.filter(
      (copy) =>
        isNonEmptyText(copy.description) &&
        getMetricDetailSections(copy).length > 0 &&
        getMetricDetailSections(copy).every(
          (section) =>
            isNonEmptyText(section.title) &&
            isNonEmptyText(section.description) &&
            isNonEmptyText(section.askSsolQuestion) &&
            section.items.some((item) => isNonEmptyText(item)),
        ),
    ).length;

    const routinePolicies = Object.values(config.routinePolicies);
    const completedRoutinePolicies = routinePolicies.filter(
      (policy) =>
        policy.steps.length > 0 &&
        policy.steps.every(
          (step) =>
            isNonEmptyText(step.name) &&
            step.scheduleTags.length > 0 &&
            isNonEmptyText(step.description) &&
            isNonEmptyText(step.category1Id) &&
            isNonEmptyText(step.selectedCategoryId),
        ),
    ).length;

    const surveyOptions = config.survey.questions.flatMap((question) => question.options);
    const completedSurveyOptions = surveyOptions.filter(
      (option) => isNonEmptyText(option.value) && isNonEmptyText(option.label) && isNonEmptyText(option.description),
    ).length;

    return {
      metricCopy: {
        complete: completedMetricCopyRanges,
        total: metricCopyRanges.length,
      },
      routinePolicy: {
        complete: completedRoutinePolicies,
        total: routinePolicies.length,
      },
      surveyOptions: {
        complete: completedSurveyOptions,
        total: surveyOptions.length,
      },
    };
  }, [config]);

  const setScopedMetric = (tab: MetricScopedTab, metricKey: SkinAnalysisMetricKey) => {
    setSelectedMetrics((current) => ({
      ...current,
      [tab]: metricKey,
    }));
  };

  const updateDraftConfig = (nextConfig: SkinAnalysisAdminConfigBundle) => {
    draftVersionRef.current += 1;
    setConfig(nextConfig);
    setHasUnsavedChanges(true);
    setMessage(null);
  };

  const saveConfig = async () => {
    if (!config || isLoading || isSaving) {
      return;
    }
    const configToSave = config;
    const versionToSave = draftVersionRef.current;
    setError(null);
    setMessage(null);
    setIsSaving(true);
    try {
      const saved = await client.saveConfig(configToSave);
      if (draftVersionRef.current === versionToSave) {
        setConfig(saved);
        setHasUnsavedChanges(false);
        setMessage('Saved Skin Analysis P1 config.');
      } else {
        setMessage('Saved the submitted config. Newer edits remain unsaved.');
      }
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save Skin Analysis config.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !config) {
    return (
      <div className="d-flex align-items-center gap-2">
        <Spinner animation="border" size="sm" />
        <span>Loading Skin Analysis config...</span>
      </div>
    );
  }

  if (!config) {
    return <Alert variant="danger">{error ?? 'Skin Analysis config is not available.'}</Alert>;
  }

  return (
    <div className="container-fluid py-2">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h1 className="h3 mb-1">Skin Analysis P1 Config</h1>
          <div className="text-muted small">
            Manage demo copy, survey mapping, and metric-based time-tagged routine policies.
          </div>
        </div>
        <div className="d-flex gap-2">
          {hasUnsavedChanges ? (
            <Badge bg="warning" text="dark" className="align-self-center">
              Unsaved changes
            </Badge>
          ) : (
            <Badge bg="success" className="align-self-center">
              Saved
            </Badge>
          )}
          <Button variant="primary" onClick={saveConfig} disabled={isLoading || isSaving || !hasUnsavedChanges}>
            {isSaving ? 'Saving...' : 'Save published config'}
          </Button>
        </div>
      </div>

      {message && <Alert variant="success">{message}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}

      {completionSummary && (
        <Row className="g-2 mb-3">
          <Col md={4}>
            <Card>
              <Card.Body className="py-2">
                <div className="small text-muted">Metric copy</div>
                <div className="fw-semibold">
                  {completionSummary.metricCopy.complete}/{completionSummary.metricCopy.total} ranges
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Body className="py-2">
                <div className="small text-muted">Routine policy</div>
                <div className="fw-semibold">
                  {completionSummary.routinePolicy.complete}/{completionSummary.routinePolicy.total} policies
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Body className="py-2">
                <div className="small text-muted">Survey options</div>
                <div className="fw-semibold">
                  {completionSummary.surveyOptions.complete}/{completionSummary.surveyOptions.total} options
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>Survey title</Form.Label>
              <Form.Control
                value={config.survey.title}
                onChange={(event) =>
                  updateDraftConfig({
                    ...config,
                    survey: {
                      ...config.survey,
                      title: event.target.value,
                    },
                  })
                }
              />
            </Col>
            <Col md={6}>
              <Form.Label>Survey key</Form.Label>
              <Form.Control
                value={config.survey.surveyKey}
                onChange={(event) =>
                  updateDraftConfig({
                    ...config,
                    survey: {
                      ...config.survey,
                      surveyKey: event.target.value,
                    },
                  })
                }
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs
        activeKey={activeTab}
        onSelect={(key) => setActiveTab((key as ConfigTab) ?? 'metric-copy')}
        className="mb-3"
        transition={false}
      >
        <Tab eventKey="metric-copy" title="Metric copy">
          <Card>
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <h2 className="h5 mb-1">{metricCopyMetricDefinition?.label ?? metricCopyMetric}</h2>
                  <div className="text-muted small">{metricCopyMetricDefinition?.description}</div>
                </div>
                <div className="d-flex align-items-end gap-3">
                  <Form.Group controlId="metric-copy-metric">
                    <Form.Label>Metric copy metric</Form.Label>
                    <Form.Select
                      value={metricCopyMetric}
                      onChange={(event) => setScopedMetric('metric-copy', event.target.value as SkinAnalysisMetricKey)}
                    >
                      {config.metrics.map((metric) => (
                        <option key={metric.key} value={metric.key}>
                          {metric.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  {metricCopyMetricDefinition?.topConcernEligible ? (
                    <Badge bg="success" className="mb-2">
                      Report metric
                    </Badge>
                  ) : (
                    <Badge bg="secondary" className="mb-2">
                      Modifier
                    </Badge>
                  )}
                </div>
              </div>
              <Form.Group className="mb-3" controlId="metric-copy-ask-ssol-question">
                <Form.Label>Metric Ask Ssol question</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  aria-label={`${metricCopyMetricDefinition?.label ?? metricCopyMetric} metric Ask Ssol question`}
                  value={config.metricCopies[metricCopyMetric].askSsolQuestion ?? ''}
                  onChange={(event) =>
                    updateDraftConfig(
                      updateMetricCopyConfig(config, metricCopyMetric, {
                        askSsolQuestion: event.target.value,
                      }),
                    )
                  }
                />
                <Form.Text className="text-muted">
                  Used when the user taps the question mark next to this metric score. Range-specific descriptions stay
                  below.
                </Form.Text>
              </Form.Group>
              <div className="d-flex flex-column gap-3">
                {config.metricCopies[metricCopyMetric].ranges.map((copy, index) => (
                  <Card key={`${metricCopyMetric}-${copy.rangeStart}-${copy.rangeEnd}`}>
                    <Card.Body>
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        <div>
                          <Badge bg="secondary">
                            Range {copy.rangeStart}-{copy.rangeEnd}
                          </Badge>
                        </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => updateDraftConfig(addMetricCopyDetailSection(config, metricCopyMetric, index))}
                          aria-label={`Add detail row to ${metricCopyMetricDefinition?.label ?? metricCopyMetric} ${copy.rangeStart}-${copy.rangeEnd}`}
                        >
                          Add detail row
                        </Button>
                      </div>
                      <Row className="g-3 mb-3">
                        <Col md={12}>
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            size="sm"
                            value={copy.description}
                            onChange={(event) =>
                              updateDraftConfig(
                                updateMetricCopyRange(config, metricCopyMetric, index, {
                                  description: event.target.value,
                                }),
                              )
                            }
                          />
                        </Col>
                      </Row>
                      <div className="d-flex flex-column gap-2">
                        {getMetricDetailSections(copy).map((section, sectionIndex) => (
                          <Card key={section.sectionId || `${copy.rangeStart}-${sectionIndex}`} className="border">
                            <Card.Body className="py-3">
                              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                <div className="fw-semibold">Detail row {sectionIndex + 1}</div>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() =>
                                    updateDraftConfig(
                                      removeMetricCopyDetailSection(config, metricCopyMetric, index, sectionIndex),
                                    )
                                  }
                                  aria-label={`Delete detail row ${sectionIndex + 1} from ${metricCopyMetricDefinition?.label ?? metricCopyMetric} ${copy.rangeStart}-${copy.rangeEnd}`}
                                >
                                  Delete row
                                </Button>
                              </div>
                              <Row className="g-3">
                                <Col md={3}>
                                  <Form.Label>Detail title</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    aria-label={`${metricCopyMetricDefinition?.label ?? metricCopyMetric} ${copy.rangeStart}-${copy.rangeEnd} detail row ${sectionIndex + 1} title`}
                                    value={section.title}
                                    onChange={(event) =>
                                      updateDraftConfig(
                                        updateMetricCopyDetailSection(config, metricCopyMetric, index, sectionIndex, {
                                          title: event.target.value,
                                        }),
                                      )
                                    }
                                  />
                                </Col>
                                <Col md={4}>
                                  <Form.Label>Detail description</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={2}
                                    size="sm"
                                    aria-label={`${metricCopyMetricDefinition?.label ?? metricCopyMetric} ${copy.rangeStart}-${copy.rangeEnd} detail row ${sectionIndex + 1} description`}
                                    value={section.description ?? ''}
                                    onChange={(event) =>
                                      updateDraftConfig(
                                        updateMetricCopyDetailSection(config, metricCopyMetric, index, sectionIndex, {
                                          description: event.target.value,
                                        }),
                                      )
                                    }
                                  />
                                </Col>
                                <Col md={4}>
                                  <Form.Label>Detail Ask Ssol question</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={2}
                                    size="sm"
                                    aria-label={`${metricCopyMetricDefinition?.label ?? metricCopyMetric} ${copy.rangeStart}-${copy.rangeEnd} detail row ${sectionIndex + 1} Ask Ssol question`}
                                    value={section.askSsolQuestion ?? ''}
                                    onChange={(event) =>
                                      updateDraftConfig(
                                        updateMetricCopyDetailSection(config, metricCopyMetric, index, sectionIndex, {
                                          askSsolQuestion: event.target.value,
                                        }),
                                      )
                                    }
                                  />
                                </Col>
                                <Col md={1}>
                                  <Form.Label>Items</Form.Label>
                                  <Form.Select
                                    size="sm"
                                    aria-label={`${metricCopyMetricDefinition?.label ?? metricCopyMetric} ${copy.rangeStart}-${copy.rangeEnd} detail row ${sectionIndex + 1} item display`}
                                    value={section.itemDisplayType}
                                    onChange={(event) =>
                                      updateDraftConfig(
                                        updateMetricCopyDetailSection(config, metricCopyMetric, index, sectionIndex, {
                                          itemDisplayType: event.target
                                            .value as SkinAnalysisMetricDetailItemDisplayType,
                                        }),
                                      )
                                    }
                                  >
                                    <option value="bullets">Bullets</option>
                                    <option value="tags">Tags</option>
                                  </Form.Select>
                                </Col>
                              </Row>
                              <div className="mt-3 d-flex flex-column gap-2">
                                {(section.items ?? []).map((item, itemIndex) => (
                                  <div key={`${section.sectionId}-${itemIndex}`} className="d-flex gap-2">
                                    <Form.Control
                                      size="sm"
                                      aria-label={`${metricCopyMetricDefinition?.label ?? metricCopyMetric} ${copy.rangeStart}-${copy.rangeEnd} detail row ${sectionIndex + 1} item ${itemIndex + 1}`}
                                      value={item}
                                      onChange={(event) =>
                                        updateDraftConfig(
                                          updateMetricCopyDetailItem(
                                            config,
                                            metricCopyMetric,
                                            index,
                                            sectionIndex,
                                            itemIndex,
                                            event.target.value,
                                          ),
                                        )
                                      }
                                    />
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() =>
                                        updateDraftConfig(
                                          removeMetricCopyDetailItem(
                                            config,
                                            metricCopyMetric,
                                            index,
                                            sectionIndex,
                                            itemIndex,
                                          ),
                                        )
                                      }
                                      aria-label={`Delete item ${itemIndex + 1} from detail row ${sectionIndex + 1}`}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="align-self-start"
                                  onClick={() =>
                                    updateDraftConfig(
                                      addMetricCopyDetailItem(config, metricCopyMetric, index, sectionIndex),
                                    )
                                  }
                                  aria-label={`Add item to detail row ${sectionIndex + 1}`}
                                >
                                  Add item
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="survey" title="Survey">
          <Card>
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                <div className="text-muted small">
                  Question IDs are generated automatically and are not editable in the admin form.
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => updateDraftConfig(addSurveyQuestion(config))}
                >
                  Add question
                </Button>
              </div>
              {config.survey.questions.map((question, questionIndex) => (
                <Card key={question.questionId} className="mb-3">
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                      <div className="fw-semibold">Question {questionIndex + 1}</div>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => updateDraftConfig(moveSurveyQuestion(config, questionIndex, -1))}
                          disabled={questionIndex === 0}
                          aria-label={`Move question ${questionIndex + 1} up`}
                        >
                          Up
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => updateDraftConfig(moveSurveyQuestion(config, questionIndex, 1))}
                          disabled={questionIndex === config.survey.questions.length - 1}
                          aria-label={`Move question ${questionIndex + 1} down`}
                        >
                          Down
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => updateDraftConfig(removeSurveyQuestion(config, questionIndex))}
                          aria-label={`Delete question ${questionIndex + 1}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                    <Row className="g-3">
                      <Col md={5}>
                        <Form.Label>Label</Form.Label>
                        <Form.Control
                          value={question.label}
                          onChange={(event) =>
                            updateDraftConfig(
                              updateSurveyQuestion(config, questionIndex, { label: event.target.value }),
                            )
                          }
                        />
                      </Col>
                      <Col md={7}>
                        <Form.Label>Prompt</Form.Label>
                        <Form.Control
                          value={question.prompt}
                          onChange={(event) =>
                            updateDraftConfig(
                              updateSurveyQuestion(config, questionIndex, { prompt: event.target.value }),
                            )
                          }
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Check
                          type="checkbox"
                          id={`question-${question.questionId}-multi-select`}
                          label="Allow multiple selections"
                          checked={question.type === 'multi' || question.type === 'ranked_multi'}
                          onChange={(event) =>
                            updateDraftConfig(
                              updateSurveyQuestion(config, questionIndex, {
                                type: event.target.checked ? 'multi' : 'single',
                              }),
                            )
                          }
                        />
                        <div className="text-muted small">
                          When enabled, the app can render this question as checkbox options.
                        </div>
                      </Col>
                    </Row>
                    <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
                      <Form.Label className="mb-0">Options</Form.Label>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => updateDraftConfig(addSurveyOption(config, questionIndex))}
                        aria-label={`Add option to ${question.label || question.questionId}`}
                      >
                        Add option
                      </Button>
                    </div>
                    <div className="d-grid gap-2">
                      {question.options.map((option, optionIndex) => {
                        const optionLabelPrefix = `Question ${questionIndex + 1} option ${optionIndex + 1}`;
                        const selectedMetricMapping = option.metricMappings?.[0]?.metricKey ?? '';

                        return (
                          <div className="border rounded p-2" key={`${question.questionId}-option-${optionIndex}`}>
                            <Row className="g-2 align-items-end">
                              <Col md={2}>
                                <Form.Label className="small text-muted">{`Option ${optionIndex + 1} value`}</Form.Label>
                                <Form.Control
                                  size="sm"
                                  aria-label={`${optionLabelPrefix} value`}
                                  value={option.value}
                                  onChange={(event) =>
                                    updateDraftConfig(
                                      updateSurveyOption(config, questionIndex, optionIndex, {
                                        value: event.target.value,
                                      }),
                                    )
                                  }
                                />
                              </Col>
                              <Col md={2}>
                                <Form.Label className="small text-muted">Visible label</Form.Label>
                                <Form.Control
                                  size="sm"
                                  aria-label={`${optionLabelPrefix} visible label`}
                                  value={option.label}
                                  onChange={(event) =>
                                    updateDraftConfig(
                                      updateSurveyOption(config, questionIndex, optionIndex, {
                                        label: event.target.value,
                                      }),
                                    )
                                  }
                                />
                              </Col>
                              <Col md={3}>
                                <Form.Label className="small text-muted">Description</Form.Label>
                                <Form.Control
                                  size="sm"
                                  aria-label={`${optionLabelPrefix} description`}
                                  value={option.description ?? ''}
                                  onChange={(event) =>
                                    updateDraftConfig(
                                      updateSurveyOption(config, questionIndex, optionIndex, {
                                        description: event.target.value,
                                      }),
                                    )
                                  }
                                />
                              </Col>
                              <Col md={2}>
                                <Form.Label className="small text-muted">Icon URL</Form.Label>
                                <Form.Control
                                  size="sm"
                                  aria-label={`${optionLabelPrefix} icon URL`}
                                  value={option.iconImageUrl ?? ''}
                                  onChange={(event) =>
                                    updateDraftConfig(
                                      updateSurveyOption(config, questionIndex, optionIndex, {
                                        iconImageUrl: event.target.value,
                                      }),
                                    )
                                  }
                                />
                              </Col>
                              <Col md={2}>
                                <Form.Label className="small text-muted">Metric mapping</Form.Label>
                                <Form.Select
                                  size="sm"
                                  aria-label={`${optionLabelPrefix} metric mapping`}
                                  value={selectedMetricMapping}
                                  onChange={(event) =>
                                    updateDraftConfig(
                                      updateSurveyOptionMetricMapping(
                                        config,
                                        questionIndex,
                                        optionIndex,
                                        event.target.value,
                                      ),
                                    )
                                  }
                                >
                                  <option value="">None</option>
                                  {config.metrics.map((metric) => (
                                    <option key={metric.key} value={metric.key}>
                                      {metric.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Col>
                              <Col md={1} className="d-flex flex-column gap-1 align-items-stretch">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() =>
                                    updateDraftConfig(moveSurveyOption(config, questionIndex, optionIndex, -1))
                                  }
                                  disabled={optionIndex === 0}
                                  aria-label={`Move option ${optionIndex + 1} up in ${question.label || question.questionId}`}
                                >
                                  Up
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() =>
                                    updateDraftConfig(moveSurveyOption(config, questionIndex, optionIndex, 1))
                                  }
                                  disabled={optionIndex === question.options.length - 1}
                                  aria-label={`Move option ${optionIndex + 1} down in ${question.label || question.questionId}`}
                                >
                                  Down
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() =>
                                    updateDraftConfig(removeSurveyOption(config, questionIndex, optionIndex))
                                  }
                                  aria-label={`Remove option ${optionIndex + 1} from ${question.label || question.questionId}`}
                                >
                                  Delete
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        );
                      })}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="routine-policy" title="Routine policy">
          <Card>
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <h2 className="h5 mb-1">{routinePolicyMetricDefinition?.label ?? routinePolicyMetric}</h2>
                  <div className="text-muted small">{routinePolicyMetricDefinition?.description}</div>
                </div>
                <Form.Group controlId="routine-policy-metric">
                  <Form.Label>Routine policy metric</Form.Label>
                  <Form.Select
                    value={routinePolicyMetric}
                    onChange={(event) => setScopedMetric('routine-policy', event.target.value as SkinAnalysisMetricKey)}
                  >
                    {config.metrics.map((metric) => (
                      <option key={metric.key} value={metric.key}>
                        {metric.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                <h2 className="h6 mb-0">
                  Routine steps for {routinePolicyMetricDefinition?.label ?? routinePolicyMetric}
                </h2>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => updateDraftConfig(addRoutineStep(config, routinePolicyMetric))}
                  aria-label={`Add step to ${routinePolicyMetricDefinition?.label ?? routinePolicyMetric} routine`}
                >
                  Add step
                </Button>
              </div>
              <Table responsive bordered hover size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Order</th>
                    <th style={{ width: 110 }}>When</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Category 1</th>
                    <th>Category 2</th>
                    <th>Category 3</th>
                    <th>Reason</th>
                    <th style={{ width: 110 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {config.routinePolicies[routinePolicyMetric].steps.map((stepItem, index) => {
                    const category2Options = getChildCategoryOptions(ROUTINE_CATEGORY_OPTIONS, stepItem.category1Id);
                    const category3Options = getChildCategoryOptions(category2Options, stepItem.category2Id);

                    return (
                      <tr key={`${routinePolicyMetric}-${stepItem.stepOrder}`}>
                        <td>{stepItem.stepOrder}</td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            {(['AM', 'PM'] as const).map((tag) => (
                              <Form.Check
                                key={tag}
                                type="checkbox"
                                label={tag}
                                aria-label={`Step ${stepItem.stepOrder} ${tag} tag`}
                                checked={stepItem.scheduleTags.includes(tag)}
                                onChange={(event) =>
                                  updateDraftConfig(
                                    updateRoutineStepScheduleTag(
                                      config,
                                      routinePolicyMetric,
                                      index,
                                      tag,
                                      event.target.checked,
                                    ),
                                  )
                                }
                              />
                            ))}
                          </div>
                        </td>
                        <td>
                          <Form.Control
                            size="sm"
                            aria-label={`Step ${stepItem.stepOrder} name`}
                            value={stepItem.name}
                            onChange={(event) =>
                              updateDraftConfig(
                                updateRoutineStep(config, routinePolicyMetric, index, {
                                  name: event.target.value,
                                }),
                              )
                            }
                          />
                        </td>
                        <td>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            size="sm"
                            aria-label={`Step ${stepItem.stepOrder} description`}
                            value={stepItem.description ?? ''}
                            onChange={(event) =>
                              updateDraftConfig(
                                updateRoutineStep(config, routinePolicyMetric, index, {
                                  description: event.target.value,
                                }),
                              )
                            }
                          />
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            aria-label={`Step ${stepItem.stepOrder} category 1`}
                            value={stepItem.category1Id}
                            onChange={(event) =>
                              updateDraftConfig(
                                updateRoutineStepCategory(config, routinePolicyMetric, index, 1, event.target.value),
                              )
                            }
                          >
                            <option value="">Select category</option>
                            {ROUTINE_CATEGORY_OPTIONS.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            aria-label={`Step ${stepItem.stepOrder} category 2`}
                            value={stepItem.category2Id ?? ''}
                            onChange={(event) =>
                              updateDraftConfig(
                                updateRoutineStepCategory(config, routinePolicyMetric, index, 2, event.target.value),
                              )
                            }
                            disabled={!stepItem.category1Id}
                          >
                            <option value="">Optional</option>
                            {category2Options.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            aria-label={`Step ${stepItem.stepOrder} category 3`}
                            value={stepItem.category3Id ?? ''}
                            onChange={(event) =>
                              updateDraftConfig(
                                updateRoutineStepCategory(config, routinePolicyMetric, index, 3, event.target.value),
                              )
                            }
                            disabled={!stepItem.category2Id}
                          >
                            <option value="">Optional</option>
                            {category3Options.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            size="sm"
                            aria-label={`Step ${stepItem.stepOrder} reason`}
                            value={stepItem.reason ?? ''}
                            onChange={(event) =>
                              updateDraftConfig(
                                updateRoutineStep(config, routinePolicyMetric, index, {
                                  reason: event.target.value,
                                }),
                              )
                            }
                          />
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1 align-items-stretch">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => updateDraftConfig(moveRoutineStep(config, routinePolicyMetric, index, -1))}
                              disabled={index === 0}
                              aria-label={`Move step ${stepItem.stepOrder} up in ${routinePolicyMetricDefinition?.label ?? routinePolicyMetric} routine`}
                            >
                              Up
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => updateDraftConfig(moveRoutineStep(config, routinePolicyMetric, index, 1))}
                              disabled={index === config.routinePolicies[routinePolicyMetric].steps.length - 1}
                              aria-label={`Move step ${stepItem.stepOrder} down in ${routinePolicyMetricDefinition?.label ?? routinePolicyMetric} routine`}
                            >
                              Down
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => updateDraftConfig(removeRoutineStep(config, routinePolicyMetric, index))}
                              aria-label={`Delete step ${stepItem.stepOrder} from ${routinePolicyMetricDefinition?.label ?? routinePolicyMetric} routine`}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
}
